import { requestToken, requestTokenImage } from "../api/request_methods";

const API_ROUTE = `${process.env.REACT_APP_NODE_URL}/api/chat`;

/**
 * Attachments are auth-scoped, so this URL cannot go straight into an <img
 * src> - fetch it with the bearer token and render the blob (see AuthImage).
 */
export const chatImageUrl = (filename) =>
  filename ? `${API_ROUTE}/image/${filename}` : null;

/**
 * Support chat for staff. The server scopes every call to the conversations
 * this account may see (own players for Admin/Outlet, everything for Super
 * Admin), so no role branching is needed here.
 */
class ChatServices {
  async getConversations(page = 1, limit = 20, filter) {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (filter) params.set("filter", filter);
      return await requestToken.get(`${API_ROUTE}/conversations?${params}`);
    } catch (error) {
      console.error("Error in getConversations:", error);
      throw error;
    }
  }

  /**
   * Newest first; the thread view reverses for display.
   *
   * `beforeId` walks backwards through history - pass the oldest id currently
   * on screen. Cursor rather than page number, so a message arriving mid-scroll
   * cannot shift the window and skip a row.
   */
  async getMessages(userId, limit = 30, beforeId) {
    try {
      const params = new URLSearchParams({
        user_id: String(userId),
        limit: String(limit),
      });
      if (beforeId) params.set("before_id", String(beforeId));
      return await requestToken.get(`${API_ROUTE}/messages?${params}`);
    } catch (error) {
      console.error("Error in getMessages:", error);
      throw error;
    }
  }

  /**
   * `image` is an optional File. When present the whole thing goes as
   * multipart, because the text and the attachment must land as one message
   * rather than two rows the reader has to piece together.
   */
  async sendMessage(userId, message, image) {
    try {
      if (image) {
        const form = new FormData();
        form.append("user_id", String(userId));
        if (message) form.append("message", message);
        form.append("image", image);
        return await requestTokenImage.post(`${API_ROUTE}/send`, form);
      }
      return await requestToken.post(`${API_ROUTE}/send`, {
        user_id: userId,
        message,
      });
    } catch (error) {
      console.error("Error in sendMessage:", error);
      throw error;
    }
  }

  /**
   * `mode` "soft" hides the attachment but keeps the file, so it can be
   * restored; "permanent" unlinks it and reclaims the disk for good.
   */
  async deleteImage(id, mode = "soft") {
    try {
      return await requestToken.post(`${API_ROUTE}/delete-image`, { id, mode });
    } catch (error) {
      console.error("Error in deleteImage:", error);
      throw error;
    }
  }

  async restoreImage(id) {
    try {
      return await requestToken.post(`${API_ROUTE}/restore-image`, { id });
    } catch (error) {
      console.error("Error in restoreImage:", error);
      throw error;
    }
  }

  async markRead(userId) {
    try {
      return await requestToken.post(`${API_ROUTE}/mark-read`, {
        user_id: userId,
      });
    } catch (error) {
      console.error("Error in markRead:", error);
      throw error;
    }
  }

  async getUnreadCount() {
    try {
      return await requestToken.get(`${API_ROUTE}/unread-count`);
    } catch (error) {
      console.error("Error in getUnreadCount:", error);
      throw error;
    }
  }
}

export default ChatServices;
